import type { ElementType, ReactNode } from 'react';
import { Flex, theme } from 'antd';
import type { GlobalToken } from 'antd';
import styled from 'styled-components';
import skemaLogo from '../../../assets/skema_logo.png';
import { colors, radii, spacing, typography } from '../../../theme-v2';
import { Divider } from '../../Divider';
import { LibertifyLogo } from '../../Logo';

export interface ExperiencePageHeaderProps {
  /**
   * Document title shown next to the logo. Consumer-supplied
   * (translated / formatted by the app).
   */
  title: ReactNode;
  /**
   * Custom logo node. Defaults to the full Libertify logo (`LibertifyLogo`),
   * 24px tall. Pass an `<img>`, SVG, or any node to override (e.g.
   * a university brand mark, or a tenant-specific logo).
   */
  logo?: ReactNode;
  /**
   * Makes the logo a link (e.g. to the app's home): a plain anchor to
   * `logoHref`, or — for client-side navigation — the app's router link
   * passed as `logoAs` with `logoTo` (e.g. `logoAs={Link} logoTo="/home"`),
   * which then handles plain vs. modified (new tab) clicks.
   */
  logoHref?: string;
  logoAs?: ElementType;
  logoTo?: string;
  /**
   * Optional back-navigation slot, rendered at the far left, before the
   * logo. Creator-mode pages (admin) pass a "Back" button here to return
   * to the experiences list; the viewer-facing page (sales) leaves it empty.
   */
  back?: ReactNode;
  /**
   * Right-side actions slot. Apps drop their own buttons in (Save,
   * Share, Contact sender, etc.) so they own the onClick handlers,
   * analytics, and which actions are relevant per page.
   */
  actions?: ReactNode;
  className?: string;
}

// PNG imports resolve to different types per bundler:
//   - Vite (admin): a raw string URL
//   - Next.js (sales, media-player): a StaticImageData object with `.src`
// Normalize so the same component compiles + runs in both.
const resolveAssetSrc = (asset: string | { src: string }): string =>
  typeof asset === 'string' ? asset : asset.src;

const skemaLogoSrc = resolveAssetSrc(skemaLogo);

// Brand name, not UI copy: it reads the same in every locale.
const LIBERTIFY_BRAND_NAME = 'Libertify';
// Design spec: the full logo is 24px tall in the experience page header.
const HEADER_LOGO_HEIGHT = 24;

const DefaultLogo = () => (
  <LibertifyLogo height={HEADER_LOGO_HEIGHT} title={LIBERTIFY_BRAND_NAME} />
);

/**
 * SKEMA Business School wordmark, for experiences owned by the SKEMA
 * organization. Pass it through the `logo` prop rather than baking the
 * tenant check in here:
 *
 *   logo={isSkemaOrganization(organizationId) ? <SkemaLogo /> : undefined}
 */
export const SkemaLogo = () => (
  <StyledLogoImg
    src={skemaLogoSrc}
    alt="SKEMA Business School"
    width={94}
    height={28}
  />
);

/**
 * ExperiencePageHeader — top bar of the experience page.
 *
 *   [ back? ] [ logo ] | [ document title ] ── [ actions: share / save / … ]
 *
 * Stateless. Logo defaults to Libertify but is fully overridable so
 * the same shell can render the same layout with a university or
 * tenant brand. The title is truncated with ellipsis when long; the
 * actions slot is a pure passthrough.
 */
export const ExperiencePageHeader = ({
  title,
  logo,
  logoHref,
  logoAs,
  logoTo,
  back,
  actions,
  className,
}: ExperiencePageHeaderProps) => {
  const { token } = theme.useToken();
  const logoNode = logo ?? <DefaultLogo />;

  return (
    <StyledHeader className={className}>
      <StyledLeading gap={spacing.md} align="center">
        {back}
        <StyledLogoSlot>
          {logoHref || logoTo ? (
            <StyledLogoLink
              as={logoAs}
              // Forwarded to the `logoAs` link component (e.g. a router Link).
              {...(logoTo === undefined ? {} : { to: logoTo })}
              href={logoHref}
              $token={token}
            >
              {logoNode}
            </StyledLogoLink>
          ) : (
            logoNode
          )}
        </StyledLogoSlot>
        {/* As tall as the logo, so it reads as a divider between modules. */}
        <Divider
          orientation="vertical"
          spacing="none"
          length={HEADER_LOGO_HEIGHT}
        />
        <StyledTitle $token={token}>{title}</StyledTitle>
      </StyledLeading>
      {actions && (
        <StyledActions gap={spacing.sm} align="center">
          {actions}
        </StyledActions>
      )}
    </StyledHeader>
  );
};

const StyledHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.md}px;
  padding: ${spacing.sm}px ${spacing.lg}px;
  background: ${colors.surface.base};
  border-bottom: 1px solid ${colors.border.subtle};
  min-height: 56px;
`;

const StyledLeading = styled(Flex)`
  min-width: 0;
  flex: 1;
`;

const StyledLogoSlot = styled.div`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

// Same focus ring as the breadcrumb links next to it.
const StyledLogoLink = styled.a<{ $token: GlobalToken }>`
  display: inline-flex;
  align-items: center;
  border-radius: ${radii.xs}px;

  &:focus-visible {
    outline: ${({ $token }) =>
      `${$token.lineWidthBold}px solid ${$token.colorPrimary}`};
    outline-offset: ${({ $token }) => $token.lineWidthBold}px;
  }
`;

const StyledLogoImg = styled.img`
  height: 26px;
  width: auto;
  display: block;
`;

// Outline width + outline offset of the links' focus ring.
const focusRingRoom = (token: GlobalToken) => token.lineWidthBold * 2;

// Clips a long title, so it leaves room for the focus ring of a link inside
// it (e.g. a breadcrumb): padding as wide as the ring, cancelled by an equal
// negative margin so the layout doesn't move.
const StyledTitle = styled.span<{ $token: GlobalToken }>`
  padding: ${({ $token }) => focusRingRoom($token)}px;
  margin: -${({ $token }) => focusRingRoom($token)}px;
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${colors.text.primary};
  line-height: ${typography.lineHeight.tight};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const StyledActions = styled(Flex)`
  flex-shrink: 0;
`;
